/* The board, on a phone: one row per technician instead of one column.
 *
 * The grid is a wall screen's shape. Ten columns across four inches gives three
 * of them, at which point the desk is hunting in two directions for a fact that
 * used to be a glance — and the two directions are the day's *history*, which is
 * the half of the board a phone is least often picked up for. Turned on its
 * side the same data reads as a list: every technician on screen at once, the
 * live square beside their name, and the history one tap in.
 *
 * Nothing here is a second model of the board. A row IS a column — the same
 * object out of `store.listColumns`, which is `visibleColumns` in a different
 * order — so a square the grid does not have cannot appear on a phone, and
 * nothing has to be kept in step with anything.
 *
 * What a row carries, and why those and not others:
 *
 *   the name            what it is read for
 *   the live square     colour bar, service letter, client — the same three
 *                       marks the grid puts in a running cell, laid along a
 *                       line. This is the answer to "what is Maria on".
 *   turn credit         the fairness number. The grid says this by how far
 *                       down a column has filled; a list has to say it.
 *   one clock           the running countdown, or the idle clock and the next
 *                       booking. Never both sets: a row showing four numbers is
 *                       a row nobody reads.
 *
 * What it does NOT carry, deliberately: the turn numbers, the row start times,
 * the missed and skipped squares above and below, the whole of the day before
 * now. All of that is the grid, and the grid is one tap away — see the toggle in
 * the header, and `ui.boardMode`.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

export const TurnList = {
  name: 'TurnList',
  setup: function () {
    /* ---- what is running on a row ----
     *
     * The last square in the column with something live in it. Searched from
     * the bottom because that is where the newest turn is, and a column can
     * hold a finished square below a running one only in the sense that a
     * half square's two halves stop separately — which is exactly the case the
     * filter below then handles.
     */
    function liveCell(col) {
      var cells = col.cells;
      for (var i = cells.length - 1; i >= 0; i--) {
        if (cells[i].runningTurnId) return cells[i];
      }
      return null;
    }

    // One turn, or the two halves of a shared square. Both are shown: a half
    // square is two clients in one chair's worth of the rotation, and a row
    // naming one of them is a row that will be read as the whole truth.
    function liveTurns(col) {
      var cell = liveCell(col);
      if (!cell) return [];
      return cell.turns.filter(function (t) { return t.endedAt == null; });
    }

    /* ---- what the row would do with the armed service ----
     *
     * The same four answers the grid's squares give, asked the same way, so a
     * phone and the wall screen never disagree about who can take a service.
     * `cellState` in turn-board.js is the sibling of this; both are guards that
     * return immediately when nothing is armed, which is the normal state.
     */
    function armState(col) {
      if (!store.ui.armedServiceId) return null;
      if (col.skipped) return 'blocked';
      // The hatched squares the grid draws down a column that has run out of
      // day, in the one form a single row has for saying it.
      if (col.offShift) return 'blocked';
      if (col.busy) return 'blocked';
      return col.staff.serviceIds.indexOf(store.ui.armedServiceId) === -1
        ? 'off-service' : 'targetable';
    }

    // Why a row said no. The grid answers this with a shape — a greyed square,
    // a dashed one — which works when twelve of them are side by side to be
    // compared. One row at a time has nothing to be compared against, so it
    // says the reason out loud instead.
    function blockedNote(col) {
      if (col.skipped) {
        var r = store.skipReasonOf(col.staff.id);
        return col.staff.name + ' is ' +
          (r ? r.label.toLowerCase() : 'out of the rotation') + '.';
      }
      var svc = store.armedService.value;
      if (col.offShift) {
        var sh = store.shiftOf(col.staff.id);
        return sh && sh.early
          ? col.staff.name + ' finishes at ' + store.formatClock(sh.early) +
            ', and ' + (svc ? svc.name : 'that service') +
            ' would not be over by then.'
          : col.staff.name + ' is finished for the day.';
      }
      if (col.busy) return col.staff.name + ' is still on a service.';
      return col.staff.name + ' does not do ' +
        (svc ? svc.name : 'that service') + '.';
    }

    /* ---- tapping a row ----
     *
     * With a service armed the row is a column: tapping it puts the square up,
     * or says why it cannot. That is the one gesture carried over whole from
     * the grid, and it has to be, because arming is the flow the ＋ sheet hands
     * off to when the desk wants to choose the technician themselves.
     *
     * With nothing armed the row is a way in to the person, and which way in
     * depends on what they are doing — the same reasoning as an empty square
     * versus a full one, which is what `onCellClick` works out over there.
     */
    function onRowTap(col) {
      if (store.ui.armedServiceId) {
        var st = armState(col);
        if (st === 'targetable') { store.bookTurn(col.staff.id); return; }
        if (st === 'off-service') {
          // Not a refusal: the grid lets an off-service square be tapped and so
          // does this, because a technician's service list is a record of what
          // they usually do and the desk is the one standing in the room.
          store.bookTurn(col.staff.id);
          return;
        }
        store.toast(blockedNote(col), 'warn');
        return;
      }
      var live = liveCell(col);
      if (live) { store.openCellMenu(live.runningTurnId); return; }
      if (col.skipped) { openStaff(col); return; }
      // Nothing running and nothing armed: the same offer an empty square
      // makes — this tech's imminent bookings, then their service list.
      if (store.refuseWhileClosed()) return;
      store.openEmptyPick(col.staff.id);
    }

    // Skip, bring back, take off the board. A right-click on the wall screen;
    // there is no such press on a phone, so it is a button on the row.
    function openStaff(col, evt) {
      var r = evt && evt.currentTarget
        ? evt.currentTarget.getBoundingClientRect()
        : null;
      store.openStaffMenu(col.staff.id, r ? r.right : 0, r ? r.bottom : 0);
    }

    /* ---- the clocks ----
     *
     * Lifted from turn-board.js rather than shared, and that is a deliberate
     * duplication of about fifteen lines: the grid's versions are written for a
     * square 104px wide and this one has a whole row, so they differ in what
     * they are willing to spell out. What they must NOT differ on is the
     * arithmetic, so the two numbers either could get wrong — how long is left,
     * and how far a finished turn ran over — are asked of the store.
     */
    var SOON_MS = 5 * 60000;

    function remainingMs(turn) {
      if (!turn.scheduledMinutes || turn.waiting) return null;
      var end = turn.endedAt == null ? store.now.value : turn.endedAt;
      return turn.scheduledMinutes * 60000 - (end - turn.startedAt);
    }

    function elapsed(turn) {
      // Held until the client is checked in: no clock to show yet.
      if (turn.waiting && turn.endedAt == null) return 'Waiting';
      var end = turn.endedAt == null ? store.now.value : turn.endedAt;
      return store.formatDuration(end - turn.startedAt);
    }

    function timerText(turn) {
      var left = remainingMs(turn);
      if (left == null) return elapsed(turn);
      return elapsed(turn) + (left < 0
        ? ' · ' + store.formatMins(-left) + ' over'
        : ' · ' + store.formatMins(left) + ' left');
    }

    function timerState(turn) {
      var left = remainingMs(turn);
      if (left == null) return null;
      if (left < 0) return 'is-over';
      return left <= SOON_MS ? 'is-soon' : null;
    }

    // Time on their hands, frozen where the day or a break stopped it — the
    // store owns both halves of that, so this is only the subtraction.
    function idleLabel(col) {
      var since = store.idleSince(col.staff.id);
      if (since == null) return '—';
      return store.formatMins(Math.max(0, store.idleUntil(col.staff.id) - since));
    }

    // The booking coming at them: the letter it will go up as, in its own
    // colour, and when. The same chip the column header carries on the grid.
    function nextChip(col) {
      var b = store.nextBookingFor(col.staff.id);
      if (!b) return null;
      var m = Math.round((b.svc.startsAt - store.minuteNow.value) / 60000);
      return {
        letter: b.mapped ? b.mapped.abbrev : '?',
        color: b.mapped ? b.mapped.color : store.defaultServiceColor,
        requested: b.why === 'requested',
        at: store.formatClock(b.svc.startsAt),
        due: m <= 0,
        soon: m > 0 && m <= 10,
        title: 'Next: ' + store.formatClock(b.svc.startsAt) + ' — ' +
          b.svc.name + ' (' + b.appt.clientName + ')'
      };
    }

    // What a row says about itself when nothing is running on it. The reason,
    // not just the absence of one — "Done" and "Break" are different questions.
    function stateOf(col) {
      if (col.skipped) {
        var r = store.skipReasonOf(col.staff.id);
        return {
          word: r ? r.badge : 'Skip',
          title: (r ? r.label : 'Skipping') + ' — turns are going past ' +
            col.staff.name,
          out: true
        };
      }
      if (col.ownClients && !col.offShift) {
        return {
          word: 'Own',
          title: col.staff.name + ' takes only clients who ask for them — out of the rotation',
          out: true
        };
      }
      if (col.offShift) {
        var sh = store.shiftOf(col.staff.id);
        return {
          word: sh && sh.early ? 'To ' + store.formatClock(sh.early) : 'Done',
          title: sh && sh.early
            ? col.staff.name + ' finishes at ' + store.formatClock(sh.early) +
              ' — no room left today for anything they do'
            : col.staff.name + ' is finished for the day',
          out: true
        };
      }
      if (col.openHalf) {
        return {
          word: 'Owed ½',
          title: col.staff.name + ' is owed the other half of a half turn — ' +
            'next for any service',
          out: false
        };
      }
      return { word: 'Free', title: col.staff.name + ' is free', out: false };
    }

    /* ---- everything a row needs, worked out once per row ----
     *
     * The template used to ask for these one at a time, which on a list is a
     * different cost than on a grid: `nextChip` alone was read eight times to
     * draw one row — is there a booking, what letter, what colour, is it due,
     * is it soon, what does the tooltip say — and each of those walked the
     * book. Twelve technicians is a hundred passes over the day's appointments,
     * and the list redraws every second because a running turn counts in
     * seconds.
     *
     * So it is a map, and what it deliberately leaves out is the running timer.
     * That is the one thing here with a second hand; everything in here changes
     * on the minute at most, so the map survives fifty-nine of every sixty
     * redraws untouched.
     */
    var rows = Vue.computed(function () {
      return store.listColumns.value.map(function (col) {
        var live = liveTurns(col);
        return {
          col: col,
          staffId: col.staff.id,
          live: live,
          color: live.length ? store.colorOfTurn(live[0]) : null,
          idle: live.length ? null : idleLabel(col),
          next: live.length ? null : nextChip(col),
          state: live.length ? null : stateOf(col)
        };
      });
    });

    function turnTitle(t) {
      var bits = [t.serviceName];
      if (t.clientName) bits.push(t.clientName);
      if (t.sourceServiceName) bits.push(t.sourceServiceName);
      return bits.join(' · ');
    }

    /* ---- the header line ----
     *
     * Where the grid's gutter went. The turn number the floor is on is the one
     * thing off the gutter worth keeping — it is how the desk says "we are on
     * turn seven" out loud — and the count of who is free is the list's own
     * summary, read before the rows rather than by counting them.
     */
    var floorTurn = Vue.computed(function () {
      return store.floorTurnsCompleted() + 1;
    });

    var freeCount = Vue.computed(function () {
      return store.listColumns.value.filter(function (c) {
        return !c.busy && !c.skipped && !c.offShift && !c.ownClients;
      }).length;
    });

    var orderTitle = Vue.computed(function () {
      return store.ui.listOrder === 'queue'
        ? 'Sorted by the rotation — whoever is up next is at the top. ' +
          'Tap for the board’s own order.'
        : 'In the board’s column order, left to right. ' +
          'Tap to sort by who is up next.';
    });

    return {
      ui: store.ui,
      rows: rows,
      upNext: store.upNextStaffId,
      armed: store.armedService,
      suggestedStaffId: store.suggestedStaffId,
      addable: store.addableStaff,
      openAddStaff: store.openAddStaff,
      pluralTurns: store.pluralTurns,
      toggleListOrder: store.toggleListOrder,
      toggleBoardMode: store.toggleBoardMode,
      optCount: store.optionCountByTurn,
      floorTurn: floorTurn,
      freeCount: freeCount,
      orderTitle: orderTitle,
      armState: armState,
      onRowTap: onRowTap,
      openStaff: openStaff,
      timerText: timerText,
      timerState: timerState,
      turnTitle: turnTitle,
      turnColor: store.colorOfTurn
    };
  },
  template: [
    '<div class="tlist">',
    '  <div v-if="!rows.length" class="empty-state empty-state--sm">',
    '    <h2>Nobody is on the board yet</h2>',
    '    <p><span>Add the technicians who are in today.</span></p>',
    '    <button type="button" class="btn btn--primary" :disabled="!addable.length"',
    '      @click="openAddStaff">Add staff for today</button>',
    '  </div>',
    '',
    '  <template v-else>',
    /* Sticky, because it is the one line that says where in the day this is and
       scrolling a long roster must not take it away. */
    '    <div class="tlist__head">',
    '      <span class="tlist__turn">Turn <strong>{{ floorTurn }}</strong></span>',
    '      <span class="tlist__free">{{ freeCount }} free</span>',
    '      <button type="button" class="tlist__sort" :title="orderTitle"',
    '        @click="toggleListOrder">',
    '        {{ ui.listOrder === "queue" ? "Queue" : "Board" }}',
    '        <span class="tlist__sort-mark" aria-hidden="true">&#8645;</span>',
    '      </button>',
    '      <button type="button" class="tlist__grid" @click="toggleBoardMode"',
    '        title="Show the turn grid — the whole day, every column"',
    '        aria-label="Show the turn grid">',
    '        <span aria-hidden="true">&#9638;</span>',
    '      </button>',
    '    </div>',
    '',
    '    <ul class="tlist__rows">',
    '      <li v-for="r in rows" :key="r.staffId" class="trow"',
    '        :class="[armState(r.col) ? \'trow--\' + armState(r.col) : null, {',
    '          \'trow--busy\': r.col.busy,',
    /* Out of day takes the same dimming a bypass gets rather than a class of
       its own: on one row at a time the badge is what says which of the two it
       is, and a second greyed-out treatment would be a distinction with
       nothing to draw it against. */
    '          \'trow--skipped\': r.col.skipped || r.col.offShift,',
    '          \'trow--suggested\': suggestedStaffId === r.staffId }]"',
    '        :style="r.color ? { \'--svc\': r.color } : null">',
    /* The whole row is the target. A button rather than a div: it is a real
       press with a real result, and everything that gives — focus, the space
       bar, what a screen reader announces — is free that way. */
    '        <button type="button" class="trow__main" @click="onRowTap(r.col)">',
    '          <span class="trow__bar" aria-hidden="true"></span>',
    '',
    '          <span class="trow__top">',
    '            <span class="trow__name">{{ r.col.staff.name }}</span>',
    /* Whose turn it is. While something is armed the claim narrows — it is no
       longer "next for anything" but "next for THIS", which is a different
       person as soon as the service is one not everybody does. */
    '            <span v-if="!armed && upNext === r.staffId" class="trow__up">Up next</span>',
    '            <span v-else-if="armed && suggestedStaffId === r.staffId"',
    '              class="trow__up trow__up--armed">Up next</span>',
    '',
    /* What is running, in the grid's own marks: the service letter in its
       colour, then who is in the chair. Two of them when a square is shared. */
    '            <span v-if="r.live.length" class="trow__live">',
    '              <span v-for="t in r.live" :key="t.id" class="trow__svc"',
    '                :title="turnTitle(t)">',
    '                <span class="trow__letter" :style="{ \'--chip\': turnColor(t) }">',
    '                  {{ t.serviceLetter }}</span>',
    '                <span v-if="t.clientName" class="trow__client">{{ t.clientName }}</span>',
    '                <span v-else class="trow__client trow__client--none">{{ t.serviceName }}</span>',
    '                <span v-if="optCount[t.id]" class="trow__opt" aria-label="Options on this booking">+</span>',
    '                <span v-if="ui.requestsOn && t.requested" class="trow__star" title="Client request">&#9733;</span>',
    '              </span>',
    '            </span>',
    '            <span v-else class="trow__state" :class="{ \'is-out\': r.state.out }"',
    '              :title="r.state.title">{{ r.state.word }}</span>',
    '          </span>',
    '',
    '          <span class="trow__bottom">',
    '            <span class="trow__credit">{{ pluralTurns(r.col.credit) }}</span>',
    '',
    /* Running: the countdown, which is the only thing on this screen with a
       second hand. Free: how long they have had, and what is coming at them.
       Never both — four numbers on one row is a row nobody reads. */
    '            <span v-if="r.live.length" class="trow__clocks">',
    '              <span v-for="t in r.live" :key="t.id" class="trow__timer"',
    '                :class="timerState(t)">{{ timerText(t) }}</span>',
    '            </span>',
    '            <span v-else class="trow__clocks">',
    '              <span class="trow__idle"><span class="trow__key">idle</span>{{ r.idle }}</span>',
    '              <span v-if="r.next" class="trow__next"',
    '                :class="{ \'is-due\': r.next.due, \'is-soon\': r.next.soon }"',
    '                :title="r.next.title">',
    '                <span class="trow__next-svc" :style="{ \'--chip\': r.next.color }">',
    '                  {{ r.next.letter }}</span>',
    '                <span v-if="r.next.requested" class="trow__next-star"',
    '                  aria-label="Client request">&#9733;</span>',
    '                {{ r.next.at }}</span>',
    '            </span>',
    '          </span>',
    /* No label on a row that is refusing what is armed, though there was one
       here at first. It read "in service" across the middle of the row, on top
       of the client's name and the countdown — which are, between them, already
       the reason. A row that is out of the rotation says so in the same place,
       in its own badge. The dimming says "not this one" and the row itself says
       why; a third voice had nothing left to add and no room to say it in. */
    '        </button>',
    '',
    '        <button type="button" class="trow__more" @click="openStaff(r.col, $event)"',
    '          :title="r.col.staff.name + \' — skip, bring back, or take off the board\'"',
    '          :aria-label="r.col.staff.name + \' options\'">&#8943;</button>',
    '      </li>',
    '    </ul>',
    '',
    '    <button type="button" class="tlist__add" @click="openAddStaff"',
    '      :disabled="!addable.length"',
    '      :title="addable.length ? \'Add someone else in for today\' : \'Everyone on the roster is already in\'">',
    '      <span aria-hidden="true">+</span> Add staff',
    '    </button>',
    '  </template>',
    '</div>'
  ].join('\n')
};
